import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation, Navigate, Outlet } from 'react-router-dom';
import {
  Box,
  CssBaseline,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
  Divider
} from '@mui/material';
import { ThemeProvider, GlobalStyles, useMediaQuery } from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import HeaderWithDropdown from '../../components/HeaderWithDropdown';
import { HEADER_HEIGHT } from '../../components/headerHeight';
import theme from '../../theme';
import { useAppTheme } from '../../themes/ThemeController';
import { DataContext } from '../../context/DataContext';
import { signOut, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';

const DRAWER_WIDTH = 260;

const buildMenuConfig = ({ isPlatformRoot, isTenantAdmin } = {}) => {
  // "Invitar usuarios" requiere administrar la cuenta (root de plataforma o
  // admin del propio tenant) -- antes se mostraba a cualquier usuario
  // autenticado sin filtrar por rol, aunque la pantalla igual lo rechazaba.
  const canManageInvites = Boolean(isPlatformRoot || isTenantAdmin);
  const securityItems = [
    { label: 'Usuarios', path: '/admin/home/seguridad/usuarios' },
    { label: 'Roles', path: '/admin/home/seguridad/roles' },
    { label: 'Políticas', path: '/admin/home/seguridad/politicas' },
    { label: 'Grupos', path: '/admin/home/seguridad/grupos' }
  ];
  if (canManageInvites) {
    securityItems.push({ label: 'Invitar usuarios', path: '/admin/home/seguridad/invitaciones' });
  }
  // "Correos de registro" (whitelist de signup) es solo-root: no es una
  // operación por-tenant, es global de la plataforma.
  if (isPlatformRoot) {
    securityItems.push({ label: 'Correos de registro', path: '/admin/home/seguridad/registro-permitido' });
  }

  const semanticModelsItems = [
    // "Entidades" ya no es una entrada de menú propia -- las tablas
    // (entidades) de una base semántica se consultan entrando a esa
    // base desde "Base de datos semantica" (columna "Tablas", ver
    // SemanticDatabaseCard.js), no sueltas de su base contenedora.
    { label: 'Conexiones', path: '/admin/home/semantic-models/conexiones' },
    { label: 'Base de datos semantica', path: '/admin/home/semantic-models/base-datos' },
    { label: 'Modelos semanticos', path: '/admin/home/semantic-models/modelos' },
    // Catálogo Iceberg (módulo nuevo, Fase 1 del plan "Catálogo Iceberg
    // S3"): visible para cualquier usuario autenticado, sin gate por
    // cognito:groups -- es de permiso GRANULAR (catalog.iceberg.*), igual
    // que las otras 3 entradas de esta sección, para que un admin de
    // tenant pueda asignárselo a un usuario puntual (rol "role_catalog_
    // admin") sin que necesite estar en el grupo Cognito "admin". El
    // backend autoriza estrictamente; ver IcebergCatalog.jsx.
    { label: 'Catálogo Iceberg', path: '/admin/home/semantic-models/catalogo-iceberg' },
    // Informe del mantenimiento automático de las tablas Iceberg del
    // tenant (mismo permiso catalog.iceberg.view, ver IcebergMaintenance.jsx).
    { label: 'Mantenimiento de tablas', path: '/admin/home/semantic-models/mantenimiento-tablas' }
  ];

  const sections = [
    // Tableros guardados desde el modo Dashboard del chat: de cada usuario
    // (privados), visibles para cualquier usuario autenticado.
    {
      label: 'Tableros',
      basePath: '/admin/home/tableros',
      items: [{ label: 'Mis tableros', path: '/admin/home/tableros' }]
    },
    {
      label: 'Semantic Models',
      basePath: '/admin/home/semantic-models',
      items: semanticModelsItems
    },
    {
      label: 'Espacios de trabajo',
      basePath: '/admin/home/workspaces',
      items: [
        { label: 'Espacios de análisis', path: '/admin/home/workspaces/espacios-analisis' },
        { label: 'SQL Workbook', path: '/admin/home/workspaces/sql-workbook' }
      ]
    },
    {
      label: 'Agentes',
      basePath: '/admin/home/agentes',
      items: [
        { label: 'Agentes', path: '/admin/home/agentes/listado' },
        { label: 'Bases de conocimiento', path: '/admin/home/agentes/bases-conocimiento' },
        { label: 'Consultas verificadas', path: '/admin/home/agentes/consultas-verificadas' },
        { label: 'Modelos LLM', path: '/admin/home/agentes/modelos-llm' }
      ]
    },
    {
      label: 'Seguridad',
      basePath: '/admin/home/seguridad',
      items: securityItems
    }
  ];

  // Auditoría: visible para CUALQUIER usuario autenticado (a diferencia de
  // Créditos y facturación, más abajo) -- cada usuario ve sus propias
  // preguntas por defecto, y solo un admin ve las de todo el tenant. El
  // control real de qué filas devuelve está en el backend
  // (audit_admin.py::list_invocations), esto es solo la entrada al menú.
  sections.push({
    label: 'Auditoría',
    basePath: '/admin/home/auditoria',
    items: [
      { label: 'Consultas', path: '/admin/home/auditoria/consultas' }
    ]
  });

  // Sección de créditos/facturación -- oculta para usuarios sin ninguno de
  // los dos roles (grupos de Cognito `root`/`admin`); las pantallas de
  // pricing/config global/consumo cross-tenant son root-únicamente y se
  // ocultan aparte (ver BillingCreditosLayout / cada página).
  if (isPlatformRoot || isTenantAdmin) {
    const billingItems = [
      { label: 'Mi cuenta y créditos', path: '/admin/home/creditos/cuenta' },
      { label: 'Política de crédito', path: '/admin/home/creditos/politica' },
      { label: 'Planes de suscripción', path: '/admin/home/creditos/planes' }
    ];
    if (isPlatformRoot) {
      billingItems.push(
        { label: 'Precios de IA', path: '/admin/home/creditos/precios' },
        { label: 'Precios por operación', path: '/admin/home/creditos/precios-operacion' },
        { label: 'Configuración de créditos', path: '/admin/home/creditos/configuracion' },
        { label: 'Consumo global', path: '/admin/home/creditos/consumo-global' }
      );
    }
    sections.push({
      label: 'Créditos y facturación',
      basePath: '/admin/home/creditos',
      items: billingItems
    });
  }

  // Configuración de sistema -- root-únicamente (a diferencia de la
  // sección de créditos, acá no hay caso de uso para tenant admin):
  // "Generación de gráficas" (prompt/params de Bedrock usados por
  // chart-data, ver ChartGenerationSettings.jsx), "Análisis profundo"
  // (prompts/límites del modo del chat, ver DeepAnalysisSettings.jsx), "Reglas
  // SQL por motor" (dialecto de cada base, ver SqlRulesSettings.jsx) y "Costos de
  // infraestructura" (registro interno de uso por tenant, ver
  // InfrastructureCosts.jsx).
  if (isPlatformRoot) {
    sections.push({
      label: 'Configuración de sistema',
      basePath: '/admin/home/sistema',
      items: [
        { label: 'Generación de gráficas', path: '/admin/home/sistema/generacion-graficas' },
        { label: 'Análisis profundo', path: '/admin/home/sistema/analisis-profundo' },
        { label: 'Generación de tableros', path: '/admin/home/sistema/generacion-tableros' },
        { label: 'Reglas SQL por motor', path: '/admin/home/sistema/reglas-sql' },
        { label: 'Costos de infraestructura', path: '/admin/home/sistema/costos-infraestructura' }
      ]
    });
  }

  return sections;
};

const AdminHomeLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Tema de MUI con los colores del tema activo (src/themes/*.css).
  const { muiTheme } = useAppTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  // Teléfonos y tablets (< md): el menú se superpone al contenido y empieza
  // cerrado; en PC es fijo al lado y al ocultarlo el contenido usa todo el ancho.
  // noSsr: valor real desde el primer render (sin esto el menú se abría un
  // instante en teléfonos y tablets antes de cerrarse).
  const isDrawerOverlay = useMediaQuery(theme.breakpoints.down('md'), { noSsr: true });
  const [drawerOpen, setDrawerOpen] = useState(!isDrawerOverlay);
  const [expandedSections, setExpandedSections] = useState({});
  const [userName, setUserName] = useState('');
  // `isPlatformRoot`/`isTenantAdmin` vienen de DataContext (ya deriva
  // `cognito:groups` del ID token vía fetchAuthSession) -- antes este
  // componente hacía su propia lectura redundante del mismo claim.
  const { isPlatformRoot, isTenantAdmin } = useContext(DataContext);

  const menuConfig = useMemo(
    () => buildMenuConfig({ isPlatformRoot, isTenantAdmin }),
    [isPlatformRoot, isTenantAdmin]
  );

  useEffect(() => {
    setDrawerOpen(!isDrawerOverlay);
  }, [isDrawerOverlay]);

  useEffect(() => {
    setExpandedSections((prev) => {
      const nextState = {};
      menuConfig.forEach((section) => {
        nextState[section.basePath] = prev[section.basePath] ?? false;
      });
      return nextState;
    });
  }, [menuConfig]);

  useEffect(() => {
    let isMounted = true;

    const loadUser = async () => {
      try {
        const currentUser = await getCurrentUser();
        const loginId = currentUser?.signInDetails?.loginId || '';
        const defaultName = loginId.split('@')[0] || '';
        const formattedName = defaultName
          ? defaultName.charAt(0).toUpperCase() + defaultName.slice(1).toLowerCase()
          : 'Usuario';
        if (isMounted) {
          setUserName(formattedName);
        }

        const attributes = await fetchUserAttributes();
        if (attributes?.name && isMounted) {
          setUserName(attributes.name);
        }

        if (isMounted) {
          const attributeAccountId = attributes?.['custom:account_id']
            || attributes?.account_id
            || attributes?.accountId
            || null;

          try {
            if (typeof window !== 'undefined' && window.localStorage && attributeAccountId) {
              window.localStorage.setItem('account_id', attributeAccountId);
            }
          } catch (storageError) {
            console.warn('No se pudo sincronizar los atributos en localStorage:', storageError);
          }
        }
      } catch (error) {
        console.error('Error fetching user info for admin layout', error);
      }
    };

    loadUser();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const currentPath = location.pathname;
    setExpandedSections((prev) => {
      const nextState = { ...prev };
      menuConfig.forEach((section) => {
        nextState[section.basePath] = currentPath.startsWith(section.basePath);
      });
      return nextState;
    });
  }, [location.pathname, menuConfig]);

  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const handleSectionToggle = (basePath) => {
    setExpandedSections((prev) => ({
      ...prev,
      [basePath]: !prev[basePath]
    }));
  };

  const handleNavigate = (path) => {
    navigate(path);
    if (isDrawerOverlay) {
      setDrawerOpen(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out from admin layout', error);
    }
  };

  const handleSettingsClick = () => {
    navigate('/layautapp');
  };

  const defaultRoute = '/admin/home/panel';
  if (
    location.pathname === '/admin/home' ||
    location.pathname === '/admin/home/'
  ) {
    return <Navigate to={defaultRoute} replace />;
  }

  return (
    <ThemeProvider theme={muiTheme || theme}>
      <GlobalStyles styles={{ ul: { margin: 0, padding: 0, listStyle: 'none' } }} />
      <CssBaseline />
      <Box sx={{ display: 'flex', minHeight: '100vh', backgroundColor: 'var(--wb-x-bg-000000, #000000)' }}>
        <HeaderWithDropdown
          drawerOpen={drawerOpen}
          isMobile={isMobile}
          handleDrawerToggle={toggleDrawer}
          userName={userName}
          handleSignOut={handleSignOut}
          onSettingsClick={handleSettingsClick}
        />

        <Drawer
          variant={isDrawerOverlay ? 'temporary' : 'persistent'}
          open={drawerOpen}
          onClose={toggleDrawer}
          ModalProps={{ keepMounted: true }}
          sx={{
            // El contenedor del menú fijo ocupa espacio en el layout: si
            // conserva su ancho al ocultarse, deja un hueco de 260 px. En
            // modo superpuesto no debe ocupar nada.
            width: !isDrawerOverlay && drawerOpen ? DRAWER_WIDTH : 0,
            flexShrink: 0,
            transition: (t) => t.transitions.create('width', {
              easing: t.transitions.easing.sharp,
              duration: drawerOpen ? t.transitions.duration.enteringScreen : t.transitions.duration.leavingScreen
            }),
            '& .MuiDrawer-paper': {
              width: DRAWER_WIDTH,
              boxSizing: 'border-box',
              backgroundColor: 'var(--wb-bg-surface)',
              color: 'var(--wb-text-strong)',
              borderRight: '1px solid var(--wb-border)'
            }
          }}
        >
          {/* El menú empieza debajo del encabezado fijo. */}
          <Box sx={{ height: HEADER_HEIGHT, flexShrink: 0 }} />
          <Box sx={{ overflowY: 'auto', px: 1 }}>
            <List>
              <ListItemButton
                onClick={() => handleNavigate('/layautapp')}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  color: 'var(--wb-text)',
                  '&:hover': {
                    backgroundColor: 'var(--wb-x-bg-0ea5e91f, rgba(14, 165, 233, 0.12))'
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'var(--wb-accent)' }}>
                  <ChatBubbleOutlineIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      Chat principal
                    </Typography>
                  }
                />
              </ListItemButton>
              <ListItemButton
                onClick={() => handleNavigate('/admin/home/panel')}
                selected={location.pathname === '/admin/home/panel'}
                sx={{
                  borderRadius: 1,
                  mb: 0.5,
                  color: 'var(--wb-text)',
                  '&:hover': { backgroundColor: 'var(--wb-x-bg-0ea5e91f, rgba(14, 165, 233, 0.12))' },
                  '&.Mui-selected': { backgroundColor: 'var(--wb-accent-tint)', color: 'var(--wb-accent)' }
                }}
              >
                <ListItemIcon sx={{ minWidth: 36, color: 'var(--wb-accent)' }}>
                  <SpaceDashboardOutlinedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                      Panel
                    </Typography>
                  }
                />
              </ListItemButton>
              <Divider sx={{ my: 1.5, borderColor: 'var(--wb-border-10)' }} />
              {menuConfig.map((section) => (
                <Box key={section.basePath}>
                  <ListItemButton
                    onClick={() => handleSectionToggle(section.basePath)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      color: 'var(--wb-text)'
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                          {section.label}
                        </Typography>
                      }
                    />
                    {expandedSections[section.basePath] ? <ExpandLess /> : <ExpandMore />}
                  </ListItemButton>
                  <Collapse in={expandedSections[section.basePath]} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      {section.items.map((item) => {
                        const selected = location.pathname === item.path;
                        return (
                          <ListItemButton
                            key={item.path}
                            onClick={() => handleNavigate(item.path)}
                            selected={selected}
                            sx={{
                              pl: 4,
                              mb: 0.5,
                              borderRadius: 1,
                              '&.Mui-selected': {
                                backgroundColor: 'var(--wb-accent-tint)',
                                color: 'var(--wb-accent)'
                              }
                            }}
                          >
                            <ListItemText
                              primary={
                                <Typography sx={{ fontSize: '0.9rem' }}>
                                  {item.label}
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Collapse>
                  <Divider sx={{ my: 1.5, borderColor: 'var(--wb-border-10)' }} />
                </Box>
              ))}
            </List>
          </Box>
        </Drawer>

        <Box
          component="main"
          sx={{
            flexGrow: 1,
            // Sin esto un hijo ancho (tablas, diagramas) impide que el área
            // principal se encoja y aparece scroll horizontal.
            minWidth: 0,
            bgcolor: 'var(--wb-bg-page)',
            color: 'var(--wb-text)',
            minHeight: '100vh',
          }}
        >
          {/* Mismo alto que el encabezado fijo (el <Toolbar /> de MUI mide
              48 px en teléfonos horizontales y el encabezado 56). */}
          <Box sx={{ height: HEADER_HEIGHT, flexShrink: 0 }} />
          <Box
            sx={{
              pt: { xs: 2, md: 4 },
              pr: { xs: 2, md: 4 },
              pb: { xs: 4, md: 6 },
              pl: { xs: 2, md: 1 }
            }}
          >
            <Outlet />
          </Box>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default AdminHomeLayout;

